import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import {
  assessResult,
  buildObservationMatrix,
  buildTestSeries,
  needsAttention,
  parseRange,
} from "./observationOverviewData";
import "./ObservationOverview.css";

// The "Results overview" tab of Observation Result.
//
// The same two feeds the pivot tab reads, laid out to answer the questions a
// patient actually brings: how has this test moved over time, which results
// sit outside their range, and what was in each set the lab sent. Graphs
// first, the dated table under them, the individual result sets last.

// How a result reads against its own range. Always a word and an icon as well
// as a colour, so the reading never depends on colour alone.
const LEVELS = {
  high: { label: "Above range", icon: "▲", className: "is-high" },
  low: { label: "Below range", icon: "▼", className: "is-low" },
  abnormal: { label: "Flagged", icon: "⚠", className: "is-abnormal" },
  normal: { label: "In range", icon: "●", className: "is-normal" },
  unknown: { label: "No range to compare", icon: "–", className: "is-unknown" },
};

function levelOf(cell) {
  const level = cell?.assessment?.level;
  return LEVELS[level] ? level : "unknown";
}

// Categorical palette, fixed order. Each tile is its own single-series chart
// with its own title, so the colour only has to tell tiles apart at a glance.
const SERIES_COLORS = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];

const W = 280;
const H = 150;
const PAD_L = 38;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 34;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

const shortDate = (ts) => moment(ts).format("DD MMM YY");

function niceMax(max) {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  return Math.ceil((max * 1.15) / magnitude) * magnitude;
}

// Small scales need decimals on the axis, or a 0-2 chart is labelled 0, 1, 1.
function tickLabel(t, top) {
  const decimals = top < 3 ? 2 : top < 10 ? 1 : 0;
  return String(Number(t.toFixed(decimals)));
}

function valueWithUnit(value, unit) {
  return `${value}${unit ? ` ${unit}` : ""}`;
}

function rangeText(cell, unit) {
  return cell?.range ? `Reference range ${valueWithUnit(cell.range, unit)}` : "No reference range on this result";
}

function Status({ cell }) {
  const spec = LEVELS[levelOf(cell)];
  return (
    <span className={`obv-status ${spec.className}`} title={cell?.assessment?.reason || spec.label}>
      <i aria-hidden="true">{spec.icon}</i>
      {spec.label}
    </span>
  );
}

/* ------------------------------------------------------------ graph tiles */

function GraphTile({ series, color }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const n = series.points.length;

  // The newest point's range is drawn as the band: that is what the lab
  // currently calls usual for this test.
  const lastRange = [...series.points].reverse().find((p) => p.cell.range)?.cell.range;
  const band = parseRange(lastRange);
  const values = series.points.map((p) => p.num.value);
  if (band?.lo != null) values.push(band.lo);
  if (band?.hi != null) values.push(band.hi);
  const top = niceMax(Math.max(...values, 0));

  const x = (i) => (n <= 1 ? INNER_W / 2 : (i / (n - 1)) * INNER_W) + PAD_L;
  const y = (v) => PAD_T + INNER_H - (Math.max(0, Math.min(v, top)) / top) * INNER_H;

  const linePath = series.points.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.num.value)}`).join(" ");
  const areaPath = n > 0 ? `${linePath} L ${x(n - 1)} ${PAD_T + INNER_H} L ${x(0)} ${PAD_T + INNER_H} Z` : "";
  const labelEvery = Math.max(1, Math.ceil(n / 4));

  function handleMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    let nearest = 0;
    let best = Infinity;
    series.points.forEach((p, i) => {
      const d = Math.abs(x(i) - px);
      if (d < best) {
        best = d;
        nearest = i;
      }
    });
    setHoverIdx(nearest);
  }

  const last = series.points[n - 1];
  const hovered = hoverIdx !== null ? series.points[hoverIdx] : null;
  const flagged = series.points.filter((p) => needsAttention(p.cell)).length;
  // Keep the tooltip inside the tile near either edge.
  const tipShift = hovered ? (x(hoverIdx) > W * 0.62 ? "-100%" : x(hoverIdx) < W * 0.38 ? "0%" : "-50%") : "-50%";

  return (
    <div className="obv-tile">
      <div className="obv-tile-title" title={series.code && series.code !== series.name ? `${series.name} (${series.code})` : series.name}>
        <span className="obv-tile-name">{series.name}</span>
        {flagged > 0 && (
          <span className="obv-badge is-warn" title={`${flagged} result${flagged === 1 ? "" : "s"} outside the reference range`}>
            ⚠ {flagged}
          </span>
        )}
      </div>
      <div className="obv-tile-chart">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="obv-tile-svg"
          role="img"
          aria-label={`${series.name}: ${n} result${n === 1 ? "" : "s"}, latest ${valueWithUnit(last.cell.value, series.unit)} on ${shortDate(last.ts)}`}
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {band && (
            <rect
              x={PAD_L}
              y={y(band.hi != null ? band.hi : top)}
              width={INNER_W}
              height={Math.max(0, y(band.lo != null ? band.lo : 0) - y(band.hi != null ? band.hi : top))}
              className="obv-refband"
            >
              <title>{`Reference range ${valueWithUnit(lastRange, series.unit)}`}</title>
            </rect>
          )}
          {[0, top / 2, top].map((t) => (
            <g key={t}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y(t)} y2={y(t)} className="obv-grid" />
              <text x={PAD_L - 6} y={y(t)} className="obv-ytick" textAnchor="end" dominantBaseline="middle">
                {tickLabel(t, top)}
              </text>
            </g>
          ))}
          {n > 0 && <path d={areaPath} fill={color} opacity="0.1" stroke="none" />}
          {n > 1 && (
            <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {hovered && <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={PAD_T} y2={PAD_T + INNER_H} className="obv-crosshair" />}
          {series.points.map((p, i) => (
            <g key={p.ts}>
              <circle
                cx={x(i)}
                cy={y(p.num.value)}
                r={hoverIdx === i ? 5.5 : 4}
                fill={needsAttention(p.cell) ? "var(--pp-danger)" : color}
                stroke="var(--pp-surface)"
                strokeWidth="2"
              />
              <circle
                cx={x(i)}
                cy={y(p.num.value)}
                r="12"
                fill="transparent"
                tabIndex={0}
                aria-label={`${shortDate(p.ts)}: ${valueWithUnit(p.cell.value, series.unit)}. ${rangeText(p.cell, series.unit)}. ${LEVELS[levelOf(p.cell)].label}.`}
                onFocus={() => setHoverIdx(i)}
                onBlur={() => setHoverIdx(null)}
              />
            </g>
          ))}
          {series.points.map((p, i) =>
            i % labelEvery === 0 || i === n - 1 ? (
              <text
                key={`x${p.ts}`}
                x={x(i)}
                y={H - PAD_B + 24}
                className="obv-xtick"
                textAnchor="end"
                transform={`rotate(-35 ${x(i)} ${H - PAD_B + 24})`}
              >
                {shortDate(p.ts)}
              </text>
            ) : null,
          )}
        </svg>
        {hovered && (
          <div
            className="obv-tip"
            style={{
              left: `${(x(hoverIdx) / W) * 100}%`,
              top: `${(y(hovered.num.value) / H) * 100}%`,
              transform: `translate(${tipShift}, calc(-100% - 10px))`,
            }}
          >
            <div className="obv-tip-date">{shortDate(hovered.ts)}</div>
            <div className="obv-tip-value">
              <strong>{hovered.cell.value}</strong> {series.unit}
            </div>
            <div className="obv-tip-range">{rangeText(hovered.cell, series.unit)}</div>
            <Status cell={hovered.cell} />
          </div>
        )}
      </div>
      <div className="obv-tile-foot">
        <span className="obv-dot" style={{ background: needsAttention(last.cell) ? "var(--pp-danger)" : color }} />
        <strong>{last.cell.value}</strong> {series.unit}
        <span className="obv-tile-when">{shortDate(last.ts)}</span>
      </div>
    </div>
  );
}

// A test that answers in words gets the same tile in the same grid, so it can
// sit beside the graphs. It has no line to draw, so it lists what it said.
function TextTile({ series }) {
  const last = series.points[series.points.length - 1];
  const shown = series.points.slice(-6).reverse();
  const flagged = series.points.filter((p) => needsAttention(p.cell)).length;
  return (
    <div className="obv-tile is-text">
      <div className="obv-tile-title">
        <span className="obv-tile-name">{series.name}</span>
        <span className="obv-badge" title="This test reports words rather than numbers, so it has no graph.">
          text
        </span>
        {flagged > 0 && <span className="obv-badge is-warn">⚠ {flagged}</span>}
      </div>
      <div className="obv-tile-chart">
        <table className="obv-text-table">
          <tbody>
            {shown.map((p) => (
              <tr key={p.ts} title={rangeText(p.cell, series.unit)}>
                <th scope="row">{shortDate(p.ts)}</th>
                <td className={needsAttention(p.cell) ? "is-flagged" : undefined}>
                  {valueWithUnit(p.cell.value, series.unit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {series.points.length > shown.length && (
          <p className="obv-muted">+ {series.points.length - shown.length} earlier — see the table below</p>
        )}
      </div>
      <div className="obv-tile-foot">
        <span className="obv-dot" style={{ background: needsAttention(last.cell) ? "var(--pp-danger)" : "var(--pp-n-400)" }} />
        <strong>{last.cell.value}</strong>
        <span className="obv-tile-when">{shortDate(last.ts)}</span>
      </div>
    </div>
  );
}

function GraphTiles({ series, visibleNames, leadTiles }) {
  const [attentionOnly, setAttentionOnly] = useState(false);

  // Colours follow a test's place in the full list, so a tile keeps its colour
  // when a filter changes what else is on screen.
  const colorOf = useMemo(() => {
    const map = new Map();
    series.filter((s) => s.kind === "graph").forEach((s, i) => map.set(s.name, SERIES_COLORS[i % SERIES_COLORS.length]));
    return map;
  }, [series]);

  let visible = series.filter((s) => visibleNames.has(s.name));
  if (attentionOnly) visible = visible.filter((s) => s.points.some((p) => needsAttention(p.cell)));
  const graphed = visible.filter((s) => s.kind === "graph").length;

  return (
    <section className="obv-block">
      <div className="obv-block-head">
        <h6>
          Results over time
          <span className="obv-count">
            {visible.length} of {series.length} test{series.length === 1 ? "" : "s"}
            {visible.length > 0 && ` (${graphed} graphed, ${visible.length - graphed} text)`}
          </span>
        </h6>
        <label className="obv-toggle">
          <input type="checkbox" checked={attentionOnly} onChange={(e) => setAttentionOnly(e.target.checked)} />
          Outside range only
        </label>
      </div>
      {visible.length === 0 && !leadTiles ? (
        <p className="obv-empty">No test matches the current search or selection.</p>
      ) : (
        <div className="obv-tiles">
          {leadTiles}
          {visible.map((s) =>
            s.kind === "graph" ? (
              <GraphTile key={s.name} series={s} color={colorOf.get(s.name)} />
            ) : (
              <TextTile key={s.name} series={s} />
            ),
          )}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ pivot table */

// A compact search box with its icon inside. Deliberately not `.form-control`:
// the legacy template stylesheet forces every `.form-control` to full width
// and a fixed height with !important, which stacked these one per line.
function Field({ icon, className = "", ...input }) {
  return (
    <span className={`obv-field ${className}`}>
      <i className={`bi ${icon}`} aria-hidden="true" />
      <input type="search" {...input} />
    </span>
  );
}

// One pivot cell, with the thing a number means nothing without: the range the
// lab sent with it. The card is positioned against the viewport so the table's
// own scroll box cannot clip it, and it opens on focus as well as hover.
function ResultCell({ test, date, cell }) {
  const [anchor, setAnchor] = useState(null);
  const level = levelOf(cell);
  const spec = LEVELS[level];

  function show(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 260;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = rect.bottom + 180 < window.innerHeight;
    setAnchor({ left, top: below ? rect.bottom + 6 : rect.top - 6, below });
  }

  return (
    <td
      className={`obv-cell ${spec.className}`}
      tabIndex={0}
      title={[`${test.name} · ${date}`, valueWithUnit(cell.value, cell.units), rangeText(cell), cell.assessment?.reason]
        .filter(Boolean)
        .join("\n")}
      onMouseEnter={show}
      onMouseLeave={() => setAnchor(null)}
      onFocus={show}
      onBlur={() => setAnchor(null)}
    >
      {cell.value}
      {needsAttention(cell) && (
        <i className="obv-cell-flag" aria-hidden="true">
          {spec.icon}
        </i>
      )}
      {anchor && (
        <span
          className="obv-hover"
          role="tooltip"
          style={{ left: anchor.left, top: anchor.top, transform: anchor.below ? undefined : "translateY(-100%)" }}
        >
          <span className="obv-hover-head">
            {test.name}
            <span>{date}</span>
          </span>
          {test.code && test.code !== test.name && <span className="obv-hover-sub">Code {test.code}</span>}
          <span className="obv-hover-value">
            <strong>{cell.value}</strong>
            {cell.units ? ` ${cell.units}` : ""}
          </span>
          <span className="obv-hover-range">
            {cell.range ? (
              <>
                Reference range <strong>{cell.range}</strong>
                {cell.units ? ` ${cell.units}` : ""}
              </>
            ) : (
              "No reference range on this result"
            )}
          </span>
          <span className={`obv-status ${spec.className}`}>
            <i aria-hidden="true">{spec.icon}</i>
            {cell.assessment?.reason || spec.label}
          </span>
        </span>
      )}
    </td>
  );
}

function ResultsPivot({ matrix, visibleNames, selectionStamp }) {
  const [open, setOpen] = useState(false);
  const [oldestFirst, setOldestFirst] = useState(false);
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [hideEmptyDates, setHideEmptyDates] = useState(true);
  const [testSearch, setTestSearch] = useState("");
  const [valueFilter, setValueFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");

  // Picking a test is a question about that test, and the dated answer is in
  // here - so picking one opens the table. Never on first render.
  const firstStamp = useRef(selectionStamp);
  useEffect(() => {
    if (selectionStamp !== firstStamp.current) setOpen(true);
  }, [selectionStamp]);

  const own = testSearch.trim().toLowerCase();
  const tests = matrix.tests.filter(
    (t) => visibleNames.has(t.name) && (!own || t.name.toLowerCase().includes(own)),
  );

  const hasValue = (row) => tests.some((t) => String(row.values[t.name]?.value ?? "") !== "");
  let rows = matrix.rows;
  const emptyDates = rows.length - rows.filter(hasValue).length;
  if (hideEmptyDates) rows = rows.filter(hasValue);
  if (attentionOnly) rows = rows.filter((row) => tests.some((t) => needsAttention(row.values[t.name])));
  const valueTerm = valueFilter.trim().toLowerCase();
  if (valueTerm) {
    rows = rows.filter((row) =>
      tests.some((t) => String(row.values[t.name]?.value ?? "").toLowerCase().includes(valueTerm)),
    );
  }
  const dateTerm = dateFilter.trim().toLowerCase();
  if (dateTerm) rows = rows.filter((row) => row.date.toLowerCase().includes(dateTerm));
  if (oldestFirst) rows = [...rows].reverse();

  const filtered = Boolean(own || valueTerm || dateTerm || attentionOnly);
  function clearFilters() {
    setTestSearch("");
    setValueFilter("");
    setDateFilter("");
    setAttentionOnly(false);
  }

  return (
    <section className="obv-block">
      <button type="button" className="obv-collapse" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="obv-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <h6>Results across dates</h6>
        <span className="obv-count">
          {tests.length} test{tests.length === 1 ? "" : "s"} · {rows.length} date{rows.length === 1 ? "" : "s"}
        </span>
      </button>

      {open && (
        <>
          <div className="obv-toolbar">
            <div className="obv-fields">
              <Field
                icon="bi-search"
                className="is-wide"
                placeholder="Search tests…"
                aria-label="Search tests in this table"
                value={testSearch}
                onChange={(e) => setTestSearch(e.target.value)}
              />
              <Field
                icon="bi-funnel"
                placeholder="Result, e.g. Negative"
                aria-label="Filter by result"
                title="Keep only the dates where one of the tests shown reads like this"
                value={valueFilter}
                onChange={(e) => setValueFilter(e.target.value)}
              />
              <Field
                icon="bi-calendar3"
                className="is-narrow"
                placeholder="Date, e.g. 2021"
                aria-label="Filter by date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              />
            </div>
            <div className="obv-pills">
              <button
                type="button"
                className="obv-pill"
                onClick={() => setOldestFirst((v) => !v)}
                title="Change the date order"
              >
                <i className={`bi ${oldestFirst ? "bi-sort-up" : "bi-sort-down"}`} aria-hidden="true" />
                {oldestFirst ? "Oldest first" : "Newest first"}
              </button>
              <button
                type="button"
                className={`obv-pill${hideEmptyDates ? " is-on" : ""}`}
                aria-pressed={hideEmptyDates}
                onClick={() => setHideEmptyDates((v) => !v)}
                title="Hide the dates where none of the tests shown has a result"
              >
                <i className={`bi ${hideEmptyDates ? "bi-check2" : "bi-eye-slash"}`} aria-hidden="true" />
                Hide empty dates
                {hideEmptyDates && emptyDates > 0 && <span className="obv-pill-count">{emptyDates}</span>}
              </button>
              <button
                type="button"
                className={`obv-pill${attentionOnly ? " is-on" : ""}`}
                aria-pressed={attentionOnly}
                onClick={() => setAttentionOnly((v) => !v)}
              >
                <i className={`bi ${attentionOnly ? "bi-check2" : "bi-exclamation-triangle"}`} aria-hidden="true" />
                Outside range only
              </button>
              {filtered && (
                <button type="button" className="obv-pill is-link" onClick={clearFilters}>
                  <i className="bi bi-x-lg" aria-hidden="true" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {tests.length === 0 || rows.length === 0 ? (
            <p className="obv-empty">No results match the current search or filter.</p>
          ) : (
            <div className="obv-scroll">
              <table className="obv-pivot">
                <thead>
                  <tr>
                    <th className="obv-pivot-date">Date of test</th>
                    {tests.map((t) => (
                      <th key={t.name} title={t.code && t.code !== t.name ? `${t.name} (${t.code})` : t.name}>
                        {t.name}
                        {t.unit && <div className="obv-pivot-unit">{t.unit}</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <th className="obv-pivot-date" scope="row">
                        {row.date}
                      </th>
                      {tests.map((t) => {
                        const cell = row.values[t.name];
                        if (!cell || cell.value === "") {
                          return (
                            <td key={t.name} className="obv-cell-empty">
                              ·
                            </td>
                          );
                        }
                        return <ResultCell key={t.name} test={t} date={row.date} cell={cell} />;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------ result sets */

const ORDER_STATUS = {
  271400000: "Some results available",
  271400001: "Order cancelled",
  271400002: "Order completed",
  271400003: "Order discontinued",
  271400004: "Order not found",
  271400005: "Order on hold",
  271400006: "In process",
  271400007: "Order replaced",
  271400008: "Scheduled",
};

const RESULT_STATUS = { 271400000: "Final", 271400001: "NA" };

function setDate(set) {
  const when = moment(set.transactionDateTime || set.createdOn);
  return when.isValid() ? when : null;
}

// The lines of one result set, read from the same endpoint the full HL7
// report uses, and each read against its own reference range.
async function loadSetLines(set) {
  const url = process.env.REACT_APP_OXAR_API_URL;
  const res = await axios(`${url}api/TDLObservation/GetObservationResultByObservationIdH17?id=${set.id}`);
  return (res.data?.content || []).map((r) => {
    const line = {
      name: r.text || r.identifier || "—",
      value: String(r.observationValue ?? "").trim(),
      units: r.units || "",
      range: r.referenceRange || "",
      flag: r.abnormalFlags || "",
    };
    return { ...line, assessment: assessResult(line) };
  });
}

// What a closed card says about the set inside it: how many results, and how
// they read. Loaded with the page, so nobody has to open ten sets to find the
// one with something outside its range.
function SetSummary({ entry }) {
  if (!entry || entry.status === "loading") {
    return (
      <span className="obv-set-summary is-loading">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Checking results…
      </span>
    );
  }
  if (entry.status === "error") {
    return (
      <span className="obv-set-summary">
        <span className="obv-pill-mini is-muted" title="Open the set to try again">
          Couldn&rsquo;t load
        </span>
      </span>
    );
  }

  const total = entry.lines.length;
  const flagged = entry.lines.filter(needsAttention).length;
  const inRange = entry.lines.filter((line) => line.assessment.level === "normal").length;
  return (
    <span className="obv-set-summary">
      <span className="obv-pill-mini">
        {total} result{total === 1 ? "" : "s"}
      </span>
      {flagged > 0 && (
        <span className="obv-pill-mini is-danger" title="Results outside their reference range">
          ⚠ {flagged} outside range
        </span>
      )}
      {inRange > 0 && (
        <span className="obv-pill-mini is-success" title="Results within their reference range">
          {flagged === 0 && inRange === total ? "✓ All in range" : `✓ ${inRange} in range`}
        </span>
      )}
    </span>
  );
}

// One result set as the lab sent it. The lines come from the list above, which
// reads a whole page of sets at once.
function ResultSetCard({ set, entry, onRetry, onOpenReport }) {
  const [open, setOpen] = useState(false);
  const lines = entry?.status === "done" ? entry.lines : null;
  const flagged = lines ? lines.filter(needsAttention).length : 0;
  const when = setDate(set);
  const status = RESULT_STATUS[set.status];
  const orderStatus = ORDER_STATUS[set.orderStatus];

  return (
    <div className={`obv-set${open ? " is-open" : ""}${flagged > 0 ? " has-flag" : ""}`}>
      <button type="button" className="obv-set-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="obv-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <span className="obv-set-name">{set.name || "Result set"}</span>
        {set.observationbundle && <span className="obv-badge is-accent">{set.observationbundle}</span>}
        {status && <span className="obv-badge">{status}</span>}
        <span className="obv-sp" />
        <SetSummary entry={entry} />
        <span className="obv-set-date">{when ? when.format("DD MMM YYYY") : ""}</span>
      </button>

      {open && (
        <div className="obv-set-body">
          <div className="obv-set-meta">
            {set.consultingDoctor && <span>Consulting doctor: {set.consultingDoctor}</span>}
            {orderStatus && <span>{orderStatus}</span>}
          </div>
          {entry?.status === "error" && (
            <p className="obv-error">
              These results could not be loaded.{" "}
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRetry}>
                Try again
              </button>
            </p>
          )}
          {(!entry || entry.status === "loading") && <p className="obv-muted">Loading results…</p>}
          {lines && lines.length === 0 && <p className="obv-muted">No individual results in this set.</p>}
          {lines && lines.length > 0 && (
            <div className="obv-scroll">
              <table className="obv-lines">
                <thead>
                  <tr>
                    <th>Test</th>
                    <th>Result</th>
                    <th>Units</th>
                    <th>Reference range</th>
                    <th>Reading</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={i} className={needsAttention(line) ? "is-flagged" : undefined}>
                      <td>
                        {line.name}
                      </td>
                      <td>
                        <strong>{line.value || "—"}</strong>
                      </td>
                      <td>{line.units || "—"}</td>
                      <td>{line.range || "—"}</td>
                      <td>
                        <Status cell={line} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="obv-set-actions">
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onOpenReport(set)}>
              Open full HL7 report →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const SETS_PER_PAGE = 10;

// Page numbers with the ends always reachable: 1 … 4 5 6 … 12.
function pageList(current, pages) {
  const wanted = new Set([1, pages, current - 1, current, current + 1]);
  const list = [...wanted].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out = [];
  list.forEach((n, i) => {
    if (i > 0 && n - list[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
}

function ResultSets({ sets, onOpenReport }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  // Lines per set id: { status: "loading" | "done" | "error", lines }. Kept
  // across pages, so paging back does not read the same sets again.
  const [linesById, setLinesById] = useState({});
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const sorted = useMemo(
    () => [...(sets || [])].sort((a, b) => (setDate(b)?.valueOf() || 0) - (setDate(a)?.valueOf() || 0)),
    [sets],
  );
  const pages = Math.max(1, Math.ceil(sorted.length / SETS_PER_PAGE));
  const current = Math.min(page, pages);
  const start = (current - 1) * SETS_PER_PAGE;
  const shown = sorted.slice(start, start + SETS_PER_PAGE);

  function load(set) {
    setLinesById((all) => ({ ...all, [set.id]: { status: "loading" } }));
    loadSetLines(set)
      .then((lines) => mounted.current && setLinesById((all) => ({ ...all, [set.id]: { status: "done", lines } })))
      .catch(() => mounted.current && setLinesById((all) => ({ ...all, [set.id]: { status: "error" } })));
  }

  // Every set on the page is read together, in parallel, as soon as the page
  // is on screen - not one at a time as each is opened.
  const shownKey = shown.map((set) => set.id).join("|");
  useEffect(() => {
    if (!open) return;
    shown.filter((set) => !linesById[set.id]).forEach(load);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shownKey]);

  const pageEntries = shown.map((set) => linesById[set.id]);
  const pageLoading = pageEntries.some((entry) => !entry || entry.status === "loading");
  const pageFlagged = shown.filter((set) => linesById[set.id]?.lines?.some(needsAttention)).length;

  function goTo(n) {
    setPage(n);
  }

  return (
    <section className="obv-block">
      <button type="button" className="obv-collapse" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="obv-chevron" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
        <h6>All result sets</h6>
        <span className="obv-count">
          {sorted.length} set{sorted.length === 1 ? "" : "s"}
        </span>
      </button>

      {open &&
        (sorted.length === 0 ? (
          <p className="obv-empty">No result sets on record.</p>
        ) : (
          <>
            <p className="obv-muted obv-lead">
              Open a set to see every result the lab sent with it.
              {!pageLoading && pageFlagged > 0 && (
                <span className="obv-warn">
                  {" "}
                  {pageFlagged} set{pageFlagged === 1 ? " on this page has" : "s on this page have"} a result outside
                  its range.
                </span>
              )}
            </p>
            <div className="obv-sets">
              {shown.map((set) => (
                <ResultSetCard
                  key={set.id}
                  set={set}
                  entry={linesById[set.id]}
                  onRetry={() => load(set)}
                  onOpenReport={onOpenReport}
                />
              ))}
            </div>
            <div className="obv-pager">
              <span className="obv-muted">
                Showing {start + 1}–{Math.min(start + SETS_PER_PAGE, sorted.length)} of {sorted.length}
              </span>
              <span className="obv-sp" />
              {pages > 1 && (
                <nav className="obv-pages" aria-label="Result set pages">
                  <button
                    type="button"
                    className="obv-page"
                    disabled={current === 1}
                    onClick={() => goTo(current - 1)}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {pageList(current, pages).map((n) =>
                    typeof n === "string" ? (
                      <span key={n} className="obv-page-gap">
                        …
                      </span>
                    ) : (
                      <button
                        key={n}
                        type="button"
                        className={`obv-page${n === current ? " is-current" : ""}`}
                        aria-current={n === current ? "page" : undefined}
                        onClick={() => goTo(n)}
                      >
                        {n}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    className="obv-page"
                    disabled={current === pages}
                    onClick={() => goTo(current + 1)}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </nav>
              )}
            </div>
          </>
        ))}
    </section>
  );
}

/* ---------------------------------------------------------------- section */

export default function ObservationOverview({ results, sets, loading, error, onOpenReport, leadTiles = null }) {
  const [search, setSearch] = useState("");
  // Ticking pins exactly the tests to compare. Empty means "all", so the
  // control does nothing until it is used.
  const [selected, setSelected] = useState(() => new Set());
  // Bumped on every change to the picked tests, so the table below can open
  // itself in response.
  const [selectionStamp, setSelectionStamp] = useState(0);

  const matrix = useMemo(() => buildObservationMatrix(results), [results]);
  const series = useMemo(() => buildTestSeries(matrix), [matrix]);

  const attentionByTest = useMemo(() => {
    const map = new Map();
    for (const row of matrix.rows) {
      for (const [name, cell] of Object.entries(row.values)) {
        if (needsAttention(cell)) map.set(name, (map.get(name) || 0) + 1);
      }
    }
    return map;
  }, [matrix.rows]);

  const term = search.trim().toLowerCase();
  const offered = term ? matrix.tests.filter((t) => t.name.toLowerCase().includes(term)) : matrix.tests;
  // What the graphs and the table show: the search, then the selection. One
  // set for both, so the two views never disagree about which tests are in view.
  const visibleNames = useMemo(
    () => new Set(offered.filter((t) => selected.size === 0 || selected.has(t.name)).map((t) => t.name)),
    [offered, selected],
  );

  function toggleTest(name) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setSelectionStamp((n) => n + 1);
  }

  function replaceSelection(names) {
    setSelected(new Set(names));
    setSelectionStamp((n) => n + 1);
  }

  if (loading) return <p className="obv-empty">Loading your results…</p>;

  return (
    <div className="obv">
      {error && <p className="obv-error">{error}</p>}

      {matrix.tests.length === 0 && !leadTiles ? (
        !error && <p className="obv-empty">No observation results on record yet.</p>
      ) : (
        <>
          <Field
            icon="bi-search"
            className="obv-search"
            placeholder="Search tests — filters the graphs and the table below…"
            aria-label="Search tests"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="obv-picker">
            <div className="obv-picker-head">
              <span>
                Select tests to compare
                <span className="obv-muted">{selected.size > 0 ? ` — ${selected.size} selected` : " — showing all"}</span>
              </span>
              <span className="obv-sp" />
              {selected.size > 0 && (
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => replaceSelection([])}>
                  Clear selection
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => replaceSelection([...attentionByTest.keys()])}
                disabled={attentionByTest.size === 0}
                title="Select every test with a result outside its reference range"
              >
                Select {attentionByTest.size} outside range
              </button>
            </div>
            {offered.length === 0 ? (
              <p className="obv-muted">No test name matches “{search}”.</p>
            ) : (
              <div className="obv-chips">
                {offered.map((t) => {
                  const flagged = attentionByTest.get(t.name) || 0;
                  const on = selected.has(t.name);
                  return (
                    <button
                      key={t.name}
                      type="button"
                      className={`obv-chip${on ? " is-on" : ""}${flagged ? " has-flag" : ""}`}
                      aria-pressed={on}
                      onClick={() => toggleTest(t.name)}
                      title={
                        flagged
                          ? `${flagged} result${flagged === 1 ? "" : "s"} outside the reference range`
                          : "All results within range, or no range to compare against"
                      }
                    >
                      {t.name}
                      {flagged > 0 && <i aria-hidden="true"> ⚠{flagged}</i>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <GraphTiles series={series} visibleNames={visibleNames} leadTiles={leadTiles} />
          <ResultsPivot matrix={matrix} visibleNames={visibleNames} selectionStamp={selectionStamp} />
        </>
      )}

      <ResultSets sets={sets} onOpenReport={onOpenReport} />

      <p className="obv-note">
        A result outside the reference range is not on its own a diagnosis — your clinician will go through what your
        results mean with you.
      </p>
    </div>
  );
}
